// Building readiness's Roof condition tab on an Amazon building: "Documents and warranties
// status", the roof warranty items EUSDR 3.1.4(2) names, each graded from the building's
// roof warranty documents. The rows come from the backend (amazon_items.graded_rows). See
// docs/Y2026-Q4/design-roof-warranty-documents.md.
import type { ReactNode } from "react";

import type {
  WarrantyDocument,
  WarrantyGrade,
} from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import { GapTag } from "./GapTag";
import styles from "./BuildingReadiness.module.css";

type Cite = (text: string) => ReactNode;

const STATUS_LABEL: Record<WarrantyGrade["status"], string> = {
  provided: "Provided",
  partial: "Partial",
  missing: "Missing",
};

const STATUS_TONE: Record<WarrantyGrade["status"], string> = {
  provided: styles.pillGood,
  partial: styles.pillWarn,
  missing: styles.pillBad,
};

const TIER_LABEL: Record<WarrantyGrade["tier"], string> = {
  confirmed: "Confirmed on the page",
  verifying: "Under verification",
  third_party: "Pending third party",
};

const PROBLEM_LABEL: Record<WarrantyGrade["filing_issues"][number]["problem"], string> = {
  misfiled: "Filed under the wrong building",
  truncated: "Truncated",
  wrong_address: "Another address on its face",
  other: "Other",
};

const Status = ({ row }: { row: WarrantyDocument }) => {
  if (row.not_needed) return <span className={styles.pillMuted}>Not needed here</span>;
  if (!row.grade) return <span className={styles.pillMuted}>Not graded yet</span>;
  return (
    <>
      <span className={STATUS_TONE[row.grade.status]}>
        {STATUS_LABEL[row.grade.status]}
      </span>
      {row.grade.status === "partial" && (row.grade.gaps ?? []).length > 0 && (
        <div className={styles.gapLine}>
          <GapTag
            partial={{ gaps: row.grade.gaps ?? [], held: [], to_provide: "", graded_on: "" }}
            heading="Why it is partial"
            flush
          />
        </div>
      )}
      <div className={styles.small}>
        {TIER_LABEL[row.grade.tier]}
        {row.grade.graded_on && ` · graded ${formatDay(row.grade.graded_on)}`}
      </div>
    </>
  );
};

export const WarrantyDocuments = ({
  rows,
  cite,
}: {
  rows: WarrantyDocument[];
  cite: Cite;
}) => {
  const provided = rows.filter((r) => r.grade?.status === "provided").length;
  const partial = rows.filter((r) => r.grade?.status === "partial").length;
  const counted = rows.filter((r) => !r.not_needed).length;
  const filing = rows.flatMap((r) =>
    (r.grade?.filing_issues ?? []).map((f) => ({ ...f, item: r.text })),
  );
  return (
    <div className={styles.blockRule}>
      <h4 className={styles.label}>Documents and warranties status</h4>
      <p className={styles.small}>
        {provided} of {counted} provided{partial ? `, ${partial} partial` : ""} · EUSDR 3.1.4(2), owed by Amazon before the SPPA is
        signed (MSPPA s5.5(b))
      </p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Status</th>
              <th>Evidence</th>
              <th>What must be provided</th>
              <th>Who resolves it</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.text}</td>
                <td>
                  <Status row={r} />
                </td>
                <td>
                  {r.grade?.evidence.length
                    ? r.grade.evidence.map((e, i) => (
                        <div key={`${e.cite}-${i}`}>
                          {cite(e.cite)}
                          {e.page ? `, page ${e.page}` : ""}
                        </div>
                      ))
                    : "-"}
                </td>
                <td>{r.grade?.to_provide || "-"}</td>
                <td>{r.grade?.resolver || "-"}</td>
                <td>{r.grade?.notes || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filing.length > 0 && (
        <>
          <h4 className={styles.label}>Filing errors</h4>
          <ul className={styles.list}>
            {filing.map((f, i) => (
              <li key={`${f.cite}-${i}`}>
                <span>
                  {PROBLEM_LABEL[f.problem]}: {cite(f.cite)}
                  {f.note && ` - ${f.note}`}
                  <span className={styles.src}> (for {f.item.toLowerCase()})</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
